import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Save, X, ChefHat, Beef, Salad, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";

import API from "@/utils/api";

export default function AdminCustomTaco() {
  const [config, setConfig] = useState({
    base_price: 3.50,
    tortillas: [],
    proteins: [],
    toppings: [],
    salsas: []
  });
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tortillas");

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: "", price: 0, active: true, branch_config: [] });

  const fetchConfig = async () => {
    try {
      const [configRes, branchRes] = await Promise.all([
        axios.get(`${API}/custom-taco/config`),
        axios.get(`${API}/public/branches`)
      ]);
      if (configRes.data) {
        setConfig(configRes.data);
      }
      setBranches(branchRes.data);
    } catch (error) {
      console.error("Error fetching config:", error);
      // Initialize with defaults if not found
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const saveConfig = async (newConfig) => {
    try {
      await axios.post(`${API}/admin/custom-taco/config`, newConfig, { withCredentials: true });
      setConfig(newConfig);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `${activeTab}_${Date.now()}`,
      name: formData.name,
      price: parseFloat(formData.price) || 0,
      active: formData.active,
      branch_config: formData.branch_config || []
    };

    const newConfig = { ...config };
    newConfig[activeTab] = [...(newConfig[activeTab] || []), newItem];
    saveConfig(newConfig);

    setShowAddDialog(false);
    setFormData({ name: "", price: 0, active: true, branch_config: [] });
  };

  const handleEditItem = () => {
    const newConfig = { ...config };
    newConfig[activeTab] = newConfig[activeTab].map(item =>
      item.id === editingItem.id
        ? {
          ...item,
          name: formData.name,
          price: parseFloat(formData.price) || 0,
          active: formData.active,
          branch_config: formData.branch_config || []
        }
        : item
    );
    saveConfig(newConfig);

    setEditingItem(null);
    setFormData({ name: "", price: 0, active: true, branch_config: [] });
  };

  const handleDeleteItem = (itemId) => {
    const newConfig = { ...config };
    newConfig[activeTab] = newConfig[activeTab].filter(item => item.id !== itemId);
    saveConfig(newConfig);
  };

  const handleToggleActive = (itemId) => {
    const newConfig = { ...config };
    newConfig[activeTab] = newConfig[activeTab].map(item =>
      item.id === itemId ? { ...item, active: !item.active } : item
    );
    saveConfig(newConfig);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      price: item.price,
      active: item.active,
      branch_config: item.branch_config || []
    });
  };

  const updateBranchConfig = (branchId, field, value) => {
    setFormData(prev => {
      const currentConfig = [...(prev.branch_config || [])];
      const existingIndex = currentConfig.findIndex(c => c.branch_id === branchId);

      let newEntry = existingIndex >= 0 ? { ...currentConfig[existingIndex] } : { branch_id: branchId, active: true };

      if (field === 'price') {
        newEntry.price = value === "" ? null : parseFloat(value);
      } else {
        newEntry[field] = value;
      }

      if (existingIndex >= 0) {
        currentConfig[existingIndex] = newEntry;
      } else {
        currentConfig.push(newEntry);
      }
      return { ...prev, branch_config: currentConfig };
    });
  };

  const tabs = [
    { id: "tortillas", label: "Tortillas", icon: Circle, color: "bg-yellow-500" },
    { id: "proteins", label: "Proteínas", icon: Beef, color: "bg-red-500" },
    { id: "toppings", label: "Toppings", icon: Salad, color: "bg-green-500" },
    { id: "salsas", label: "Salsas", icon: ChefHat, color: "bg-orange-500" },
  ];

  const currentItems = config[activeTab] || [];

  if (isLoading) {
    return (
      <AdminLayout title="Crea Tu Taco">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Crea Tu Taco">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Configuración del Personalizador</h1>
            <p className="text-gray-400">Gestiona ingredientes para que tus clientes personalicen sus platos</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-gray-800 px-4 py-2 rounded-lg">
              <span className="text-gray-400 text-sm">Precio base:</span>
              <Input
                type="number"
                step="0.10"
                value={config.base_price}
                onChange={(e) => {
                  const newConfig = { ...config, base_price: parseFloat(e.target.value) || 0 };
                  saveConfig(newConfig);
                }}
                className="w-20 bg-transparent border-none text-yellow-500 font-bold text-lg inline-block ml-2 p-0"
              />
              <span className="text-yellow-500 font-bold">€</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const count = (config[tab.id] || []).filter(i => i.active).length;
            return (
              <Button
                key={tab.id}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id
                  ? `${tab.color} text-black hover:opacity-90`
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Items Grid */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              {tabs.find(t => t.id === activeTab)?.label}
              <Badge className="bg-gray-700">{currentItems.length} items</Badge>
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setFormData({ name: "", price: 0, active: true });
                setShowAddDialog(true);
              }}
              className="bg-yellow-500 text-black hover:bg-yellow-400"
            >
              <Plus className="w-4 h-4 mr-1" />
              Añadir
            </Button>
          </CardHeader>
          <CardContent>
            {currentItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay items en esta categoría</p>
                <p className="text-sm">Añade el primero usando el botón de arriba</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {currentItems.map(item => (
                  <div
                    key={item.id}
                    className={`bg-gray-800 rounded-lg p-4 border-2 transition-all ${item.active ? "border-gray-700" : "border-red-500/30 opacity-60"
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-white">{item.name}</span>
                      <Badge className={item.active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {item.active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-yellow-500 font-bold">
                        {item.price > 0 ? `+${item.price.toFixed(2)} €` : "Incluido"}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(item.id)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                        >
                          <Switch checked={item.active} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(item)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-blue-400"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteItem(item.id)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog || editingItem !== null} onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditingItem(null);
          }
        }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Editar Item" : "Añadir Item"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Nombre</label>
                <Input
                  placeholder="Ej: Tortilla de maíz"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Precio adicional (€)</label>
                <Input
                  type="number"
                  step="0.10"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Usa 0 si está incluido en el precio base</p>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-400">Activo</label>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>

              {/* Branch Overrides */}
              {branches.length > 0 && (
                <div className="border-t border-gray-800 pt-4 mt-2">
                  <label className="text-sm font-semibold text-gray-300 mb-3 block">Precios y Disponibilidad por Sucursal</label>
                  <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                    {branches.map((branch) => {
                      const config = formData.branch_config?.find((c) => c.branch_id === branch.branch_id) || {};
                      const overrideActive = config.active !== false;
                      const overridePrice = config.price !== undefined && config.price !== null ? config.price : "";

                      return (
                        <div key={branch.branch_id} className="flex items-center gap-3 bg-gray-800/50 p-3 rounded-lg border border-gray-800">
                          <span className="flex-1 text-xs font-medium text-gray-300 truncate" title={branch.name}>{branch.name}</span>

                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder={formData.price || "Base"}
                              className="w-20 h-8 text-xs bg-gray-800 border-gray-700 text-white"
                              value={overridePrice}
                              onChange={(e) => updateBranchConfig(branch.branch_id, "price", e.target.value)}
                            />
                            <span className="text-[10px] text-gray-500">€</span>
                          </div>

                          <div className="w-px h-4 bg-gray-700 mx-1"></div>

                          <Switch
                            checked={overrideActive}
                            onCheckedChange={(checked) => updateBranchConfig(branch.branch_id, "active", checked)}
                            className="scale-75 data-[state=checked]:bg-green-500"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 italic">
                    * El precio vacío usa el precio base. Desactiva para ocultar en esa sucursal.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setEditingItem(null);
                }}
                className="border-gray-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={editingItem ? handleEditItem : handleAddItem}
                disabled={!formData.name.trim()}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
              >
                {editingItem ? "Guardar" : "Añadir"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
